import React, { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { pictureDetailedVODPageTestIDs, Theme } from '@constants';
import { useContextReference } from '@hooks';

import { isAndroid } from '../../../helpers/Platform';
import { FocusableFlatList } from '../../focusable/FocusableFlatList/FocusableFlatList';
import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCText } from '../../primitives/TWCText/TWCText';

const CONSTANTS = {
  height: Theme.px(90),
  width: Theme.px(150),
  spacing: Theme.px(30),
  rowOffset: Theme.px(90),
};

const tPath = 'pictureDetailedPage';

const SeasonItemContent = ({ data, isCurrentSeason, hideFocus, ...properties }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });
  const focused = properties.focused && !hideFocus;
  const style = {
    color: focused || isCurrentSeason ? Theme.whisperWhite : Theme.suvaGrey,
  };

  return (
    <View style={styles.tileContent}>
      <TWCText noTestID style={style} variant={'regularText30'}>
        {t('layout.season', { season: data.season })}
      </TWCText>
      {focused && (
        <>
          <View style={styles.bottomHighlight} />
          <TWCText noTestID style={styles.totalEpisodes} variant={'regularText20'}>
            {t('layout.totalEpisodes', { total: data.episodes.length })}
          </TWCText>
        </>
      )}
    </View>
  );
};

const SeasonItem = forwardRef(
  (
    { data, onFocus, onPress, nextFocusDown, nextFocusUp, nextFocusLeft, nextFocusRight, isCurrentSeason, hideFocus },
    reference,
  ) => {
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    return (
      <FocusableHighlight
        accessibilityHint={t('layout.seasonHint')}
        accessibilityLabel={t('layout.season', { season: data.season })}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        style={styles.tileContainer}
        testID={`pdp_season_${data.id}`}
        underlayColor={Theme.transparent}
        onFocus={onFocus}
        onPress={onPress}
      >
        <SeasonItemContent data={data} hideFocus={hideFocus} isCurrentSeason={isCurrentSeason} />
      </FocusableHighlight>
    );
  },
);

export const PictureDetailedSeasonsSelector = ({
  data,
  onLayoutFocus,
  nextFocusDown,
  nextFocusUp,
  currentSeason,
  testID,
  focusIndexChanged,
  onFocus,
  onBlur,
  onFlatlistRef,
  hideFocus,
}) => {
  const seasonReference = useContextReference(pictureDetailedVODPageTestIDs.seasonsMenu);

  const nextFocusLeft = isAndroid ? seasonReference : null;

  const _getItemLayout = useCallback(
    (_, index) => ({
      length: CONSTANTS.width,
      offset: (CONSTANTS.width + CONSTANTS.spacing) * index,
      index,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const _renderItem = useCallback(
    (_, index) => {
      const rowData = data[index];
      const isCurrentSeason = index === currentSeason;

      return (
        <SeasonItem
          data={rowData}
          hideFocus={hideFocus}
          isCurrentSeason={isCurrentSeason}
          nextFocusLeft={nextFocusLeft}
          onPress={() => {}}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, currentSeason, hideFocus],
  );

  const _keyExtractor = useCallback((_, index) => `pdp_season_item_${index + 1}`, []);

  const _separator = useCallback(() => <View style={styles.tileSeparator} />, []);

  return (
    <View style={styles.wrapper}>
      <FocusableFlatList
        containerStyle
        ItemSeparatorComponent={_separator}
        ListFooterComponent={View}
        ListFooterComponentStyle={{
          width: Theme.px(1920 - CONSTANTS.width),
        }}
        data={data}
        focusIndexChanged={focusIndexChanged}
        focusPositions={1}
        getItemLayout={_getItemLayout}
        horizontal
        itemUnderlayColor={'clear'}
        keyExtractor={_keyExtractor}
        listGainedFocus={onLayoutFocus}
        nestedScrollEnabled
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={null}
        nextFocusUp={nextFocusUp}
        paddingStart={CONSTANTS.rowOffset}
        removeClippedSubviews={false}
        renderItem={_renderItem}
        showsHorizontalScrollIndicator={false}
        testID={testID}
        wrapItem={false}
        onFocus={onFocus}
        onRef={onFlatlistRef}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bottomHighlight: {
    backgroundColor: Theme.dodgerLightBlue,
    borderRadius: Theme.px(5),
    height: Theme.px(6),
    marginBottom: Theme.px(8),
    marginTop: Theme.px(5),
    width: '100%',
  },
  tileContainer: {
    borderRadius: Theme.tileBorderRadius,
    height: CONSTANTS.height,
  },
  tileContent: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'flex-start',
  },
  tileSeparator: {
    width: Theme.px(70),
  },
  totalEpisodes: {
    color: Theme.suvaGrey,
  },
  wrapper: {
    flexDirection: 'row',
    marginBottom: Theme.px(30),
    paddingTop: Theme.px(30),
  },
});
